import React from 'react';
import { Link } from 'react-router-dom';
import { 
  SparklesIcon, 
  UserIcon, 
  GiftIcon, 
  PrinterIcon,
  CheckIcon,
  StarIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const Services: React.FC = () => {
  const services = [
    {
      id: 'branding',
      icon: <SparklesIcon className="h-12 w-12 text-accent" />,
      title: 'Corporate Branding & Signage',
      description: 'Professional branding solutions including logos, signage, and corporate identity packages that make your business stand out.',
      features: [
        'Logo Design & Brand Identity',
        'Business Cards & Stationery',
        'Signage & Display Solutions',
        'Brand Guidelines & Standards'
      ]
    },
    {
      id: 'wear',
      icon: <UserIcon className="h-12 w-12 text-accent" />,
      title: 'Corporate Wear & Uniforms',
      description: 'Custom corporate wear and uniforms designed to represent your brand professionally while ensuring comfort and durability.',
      features: [
        'Custom Uniform Design',
        'Branded Workwear',
        'Team Apparel',
        'Promotional Clothing'
      ]
    },
    {
      id: 'gifts',
      icon: <GiftIcon className="h-12 w-12 text-accent" />,
      title: 'Corporate Gifts',
      description: 'Premium corporate gifts and promotional items that leave a lasting impression on your clients and partners.',
      features: [
        'Executive Gift Sets',
        'Promotional Products',
        'Custom Branded Items',
        'Corporate Event Gifts'
      ]
    },
    {
      id: 'printing',
      icon: <PrinterIcon className="h-12 w-12 text-accent" />,
      title: 'Printing & Advertising',
      description: 'High-quality printing services and advertising solutions to promote your business effectively.',
      features: [
        'Digital & Offset Printing',
        'Banner & Display Printing',
        'Marketing Materials',
        'Large Format Printing'
      ]
    }
  ];

  const testimonials = [
    {
      text: "Electus Creative gave us everything we needed — fast, professional, and with attention to detail.",
      author: "Sarah Johnson",
      company: "Tech Solutions Ltd",
      rating: 5
    },
    {
      text: "Our branded corporate gifts were loved by all our clients — amazing quality and design!",
      author: "Michael Chen",
      company: "Global Enterprises",
      rating: 5
    },
    {
      text: "The team at Electus Creative understood our vision and delivered beyond our expectations.",
      author: "Emma Williams",
      company: "Creative Agency",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
              Comprehensive creative solutions for your business needs. From branding to corporate gifts, we deliver excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              What We Do
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We offer a complete range of creative services to help your business grow and succeed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-6">
                  {service.icon}
                  <h3 className="text-2xl font-bold text-primary ml-4">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {service.description}
                </p>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <CheckIcon className="h-5 w-5 text-accent mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/gallery?category=${service.id}`}
                  className="inline-flex items-center text-accent hover:text-primary font-medium transition-colors duration-200"
                >
                  <EyeIcon className="h-5 w-5 mr-2" />
                  View Gallery
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600">
              We follow a proven process to ensure your project exceeds expectations.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'We understand your needs and goals' },
              { step: '02', title: 'Design', description: 'Creative solutions tailored to your brand' },
              { step: '03', title: 'Production', description: 'High-quality manufacturing and printing' },
              { step: '04', title: 'Delivery', description: 'Timely delivery and ongoing support' }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="bg-accent text-white w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it - hear from our satisfied clients.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 text-lg italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-primary">{testimonial.author}</p>
                  <p className="text-gray-600">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Let's discuss your project and create something amazing together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="btn-accent text-lg px-8 py-3"
            >
              Get a Quote
            </a>
            <a
              href="/gallery"
              className="btn-outline border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-3"
            >
              View Products
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
